import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import CustomSpinner from '@components/Spinner/CustomSpinner';
import { usePaymentUpdate, useSubscribeUser } from '@hooks';
import { root_path, subscribe_referral_path } from '@routes';
import { useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { extractQueryParams } from '@utils/queryStringHelper';

import styles from './styles.module.scss';

enum StripeStatus {
  Succeeded = 'succeeded',
  Processing = 'processing',
  RequiresPaymentMethod = 'requires_payment_method',
}

type Status =
  | 'canceled'
  | 'processing'
  | 'requires_action'
  | 'requires_capture'
  | 'requires_confirmation'
  | 'requires_payment_method'
  | 'succeeded';

const PaymentStatus = () => {
  const stripe = useStripe();
  const [message, setMessage] = useState(null);
  const [subMessage, setSubMessage] = useState(null);
  const { mutate: subscribeUser } = useSubscribeUser();
  const { mutate: updatePayment } = usePaymentUpdate();
  const {
    article_id: articleId,
    code: offerCode,
    guid: referralGuid,
    modal: modalGuid,
    payment_intent_client_secret: paymentIntentClientSecret,
    presented_offer_codes: presentedOfferCodes,
    setup_intent_client_secret: setupIntentClientSecret,
    subscription_id: subscriptionId,
    update_payment: isUpdatePayment,
  } = extractQueryParams([
    'article_id',
    'code',
    'guid',
    'modal',
    'payment_intent_client_secret',
    'presented_offer_codes',
    'setup_intent_client_secret',
    'subscription_id',
    'update_payment',
  ]);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    // User shouldn't be on this page if they don't have these params
    const userShouldntAccessConfirmationPage =
      (!setupIntentClientSecret && !paymentIntentClientSecret) || (!offerCode && !isUpdatePayment);
    if (userShouldntAccessConfirmationPage) {
      toast.error("You don't have permission to access this page.");
      window.location = root_path();
      return;
    }

    const handlePaymentStatus = (status: Status, error: StripeError) => {
      if (error) {
        toast.error('Something went wrong, please try again');
        window.location.href = root_path();
        return;
      }

      switch (status) {
        case StripeStatus.Succeeded:
          setMessage('Confirming payment method.');
          setSubMessage('Please hold while your payment is being processed.');
          if (isUpdatePayment) {
            updatePayment();
          } else {
            subscribeUser({
              code: offerCode,
              modal: modalGuid,
              page: window.location.pathname,
              presented_offer_codes: presentedOfferCodes.split(','),
              referring_article_id: articleId,
              referrer_guid: referralGuid,
              source: 'web',
              subscription_id: subscriptionId,
            });
          }
          break;

        case StripeStatus.RequiresPaymentMethod:
          // Redirect your user back to your payment page to attempt collecting payment again
          setMessage('Payment method failed.');
          setSubMessage('Please hold while we redirect you back to try another payment method.');
          setTimeout(() => {
            if (referralGuid) {
              window.location.href = subscribe_referral_path(referralGuid);
            } else {
              history.back();
            }
          }, 5000);
          break;
      }
    };

    // If this is a payment method collection or update, we need to retrieve the setup intent and confirm it
    // else if this is a new subscription, we need to retrieve the payment intent and confirm it
    if (setupIntentClientSecret) {
      stripe.retrieveSetupIntent(setupIntentClientSecret).then(({ setupIntent, error }) => {
        handlePaymentStatus(setupIntent.status, error);
      });
    } else if (paymentIntentClientSecret) {
      stripe.retrievePaymentIntent(paymentIntentClientSecret).then(({ paymentIntent, error }) => {
        paymentIntent.status;
        handlePaymentStatus(paymentIntent.status, error);
      });
    }
  }, [
    articleId,
    isUpdatePayment,
    modalGuid,
    offerCode,
    presentedOfferCodes,
    paymentIntentClientSecret,
    referralGuid,
    setupIntentClientSecret,
    stripe,
    subscribeUser,
    subscriptionId,
    updatePayment,
  ]);

  return (
    <div id="payment-status" className={clsx(styles.confirmationContainer, 'container')}>
      <div className="row">
        <div className="col-md-10 col-md-offset-1">
          <div className={styles.confirmationCard}>
            <div className={styles.loader}>
              <CustomSpinner color="#d04237" size={15} />
            </div>
            <div className={styles.message}>{message}</div>
            <div className={styles.subMessage}>{subMessage}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
